import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useCreateMessageMutation } from "../../../api/chatApi";
import { ChatRequestTaskQue } from "../../../api/chatRequestTaskQueue";
import type { MessageInteractionPhase, Message, ModelOption } from "./types";
import type { Option } from "../../../components/tailwind/dropdown/Dropdown";
import { preventOverflowOnTextarea } from "../../../products/copy-assistant/utils";
import { Button, Dropdown } from "../../../components/tailwind";
import { FileDropContainer } from "../../../components/FileDrop";
import isNull from "lodash/isNull";
import { validateFileExtension } from "../../../utils/files";

const ALLOWED_EXTENSIONS = ["png", "jpg", "jpeg", "webp", "svg", "bmp", "ico"];

type ChatMessageFormProps = {
  chatId: number;
  chatRequestTaskQue: ChatRequestTaskQue;
  modelOptions: ModelOption[];
  initialModel: ModelOption;
  createMessage: ReturnType<typeof useCreateMessageMutation>[0];
  interactionPhase: MessageInteractionPhase;
  setResponsePhase: React.Dispatch<
    React.SetStateAction<MessageInteractionPhase>
  >;
  onMessageAdded: (newMessage: Message) => void;
  onAssistantStreamUpdate?: (assistant: Message) => void;
  onNonStreamResponse: () => void;
};

export const ChatMessageForm: React.FC<ChatMessageFormProps> = ({
  chatId,
  chatRequestTaskQue,
  modelOptions: modelOptionItems,
  initialModel,
  createMessage,
  interactionPhase,
  setResponsePhase,
  onMessageAdded,
  onAssistantStreamUpdate,
  onNonStreamResponse,
}) => {
  const [selectedModel, setSelectedModel] = useState<ModelOption>(initialModel);
  const [inputText, setInputText] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<File>(null);
  const textareaRef = useRef<HTMLTextAreaElement>();
  const receivedStreamChunksRef = useRef(false);
  const [fileURL, setFileURL] = useState<string>();
  const fileReader = new FileReader();
  fileReader.onload = (e: ProgressEvent<FileReader>) => {
    const dataUrl = e.target?.result as string;
    setFileURL(dataUrl);
  };

  const isSelectedFileInvalid = useMemo((): boolean => {
    if (isNull(selectedFile)) return false;
    return !validateFileExtension(selectedFile, ALLOWED_EXTENSIONS);
  }, [selectedFile]);

  const modelOptions: Option<string>[] = useMemo(
    () =>
      modelOptionItems.map((item) => ({
        key: item.model_name,
        value: item.model_name,
        text: item.display_name,
      })),
    []
  );

  useEffect(() => {
    handleModelSelect(initialModel?.model_name);
  }, [chatId]);

  useEffect(() => {
    if (!selectedFile) {
      setFileURL(undefined);
      return;
    }
    fileReader.readAsDataURL(selectedFile);
  }, [selectedFile]);

  useLayoutEffect(() => {
    if (textareaRef.current) {
      preventOverflowOnTextarea(textareaRef.current);
    }
  }, []);

  const handleModelSelect = (modelName: string): void => {
    setSelectedModel(
      modelOptionItems.find((item) => item.model_name === modelName)
    );
  };

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (!inputText.trim() || interactionPhase === "awaiting reply") {
      return;
    }

    setInputText("");
    receivedStreamChunksRef.current = false;
    setResponsePhase("creating message");

    try {
      const response = await createMessage({
        chat_id: chatId,
        text: inputText,
        model_name: selectedModel.model_name,
        image_url: fileURL,
      }).unwrap();
      setResponsePhase("awaiting reply");
      setSelectedFile(null);

      const createdMessage = response.created_message;
      const valueError = response.value_error;
      const error = response.error;

      if (error) {
        throw new Error(error);
      } else if (valueError) {
        throw new Error(valueError);
      }

      const userMessage: Message = {
        role: "user",
        text: createdMessage.text,
        timeStamp: createdMessage.timestamp,
        imageURL: createdMessage.image_url,
      };
      onMessageAdded(userMessage);

      const { response_message } = await chatRequestTaskQue.requestResponse(
        chatId,
        {
          onStreamChunk: (payload) => {
            const message = payload.messages?.[0];
            if (!message || typeof message !== "object") {
              return;
            }
            receivedStreamChunksRef.current = true;
            const row = message as {
              text?: string;
              timestamp?: string;
              html?: string;
            };
            onAssistantStreamUpdate?.({
              role: "assistant",
              text: row.text ?? "",
              timeStamp: row.timestamp ?? null,
              html: row.html,
            });
          },
        }
      );

      if (!response_message) {
        throw new Error("No response message received from chat");
      }

      const assistantMessage: Message = {
        role: "assistant",
        text: response_message.text,
        timeStamp: response_message.timestamp,
        html: response_message.html,
      };

      if (receivedStreamChunksRef.current) {
        onAssistantStreamUpdate?.(assistantMessage);
      } else {
        onMessageAdded(assistantMessage);
        onNonStreamResponse();
      }
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setResponsePhase("idle");
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="tw-w-full tw-max-w-[1000px] tw-p-6 tw-rounded-3xl tw-border tw-shadow-lg"
      id="chat-message-form"
    >
      <FileDropContainer
        selectedFile={selectedFile}
        onChange={(file): void => {
          setSelectedFile(file);
        }}
        isInvalid={isSelectedFileInvalid}
        allowedFileExtensions={ALLOWED_EXTENSIONS}
        placeholderText="Attach image"
      >
        <textarea
          value={inputText}
          id="chat-message-textarea"
          ref={textareaRef}
          onChange={(e) => setInputText(e.target.value)}
          className="tw-m-1 !tw-p-0 tw-resize-none tw-border !tw-ring-0"
          disabled={interactionPhase !== "idle"}
        />
        <div className="tw-flex tw-justify-between">
          <div className="tw-flex tw-gap-4">
            <Dropdown
              options={modelOptions}
              value={selectedModel?.model_name || null}
              onChange={handleModelSelect}
            />
          </div>
          <Button
            type="submit"
            disabled={!inputText.trim() || interactionPhase !== "idle"}
            variant="textual-blue"
          >
            Send
          </Button>
        </div>
      </FileDropContainer>
    </form>
  );
};
